import React, { memo } from 'react';
import { View, Text, StyleProp, ViewStyle } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { ClientSummary } from '../types';
import { TYPOGRAPHY } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { Avatar, Badge, Card } from './ui';

interface ClientCardProps {
  client: ClientSummary;
  onPress: (clientId: number) => void;
  style?: StyleProp<ViewStyle>;
}

function ClientCard({ client, onPress, style }: ClientCardProps) {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const isRed = client.resteAPayer > 0;
  const isAgence = client.type === 'AGENCE';

  return (
    <Card
      flush
      onPress={() => onPress(client.id)}
      style={[styles.card, style]}
      accessibilityLabel={`Ouvrir le profil de ${client.nomRaisonSociale}`}
    >
      <Avatar name={client.nomRaisonSociale} size={48} variant={isAgence ? 'brand' : 'soft'} />
      <View style={styles.info}>
        <Text style={styles.name} numberOfLines={1}>{client.nomRaisonSociale}</Text>
        <View style={styles.metaRow}>
          <Badge
            label={isAgence ? 'Agence' : 'Particulier'}
            tone={isAgence ? 'brand' : 'neutral'}
            icon={isAgence ? 'domain' : 'account-outline'}
          />
          {client.telephone ? (
            <View style={styles.phone}>
              <MaterialCommunityIcons name="phone-outline" size={14} color={colors.textSecondary} />
              <Text style={styles.phoneText} numberOfLines={1}>{client.telephone}</Text>
            </View>
          ) : null}
        </View>
      </View>
      <View style={styles.finance}>
        {isRed ? (
          <Text style={styles.resteDue} numberOfLines={1}>
            {`${client.resteAPayer.toLocaleString('fr-FR')} DH`}
          </Text>
        ) : (
          <Badge label="Soldé" tone="success" icon="check" style={styles.settledBadge} />
        )}
        <Text style={styles.financeLabel}>Reste à payer</Text>
      </View>
    </Card>
  );
}

export default memo(ClientCard);

const useStyles = makeStyles((c) => ({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingHorizontal: 16,
    paddingVertical: 16,
    marginBottom: 12,
  },
  info: { flex: 1, minWidth: 0 },
  name: { ...TYPOGRAPHY.bodyStrong, fontSize: 16, color: c.textPrimary },
  metaRow: { flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginTop: 6 },
  phone: { flexDirection: 'row', alignItems: 'center', gap: 4, flexShrink: 1 },
  phoneText: { fontSize: 13, color: c.textSecondary, flexShrink: 1 },
  finance: { alignItems: 'flex-end', maxWidth: '40%' },
  resteDue: { fontSize: 15, fontWeight: '700', color: c.danger },
  settledBadge: { alignSelf: 'flex-end' },
  financeLabel: { ...TYPOGRAPHY.caption, color: c.textMuted, marginTop: 4 },
}));
